import { HttpClient, HttpContext, HttpHeaders, HttpParams, HttpParamsOptions, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of, Subscription, throwError } from 'rxjs';
import { CookieService } from 'ngx-cookie-service';
import { environment } from 'src/environments/environment';
import { catchError, delay } from "rxjs/operators";
import { Router } from '@angular/router';
import { MatSnackBar, MatSnackBarRef } from '@angular/material/snack-bar';
import { SnackbarComponent } from 'src/app/components/snackbar/snackbar.component';
@Injectable({
  providedIn: 'root'
})
export class HttpService {
  baseUrl: string = environment.BASE_URL
  lidarBaseUrl: string = environment.LIDAR_BASE_URL
  fromLogoutPage: boolean = window.location.pathname.includes('logout')
  tokenSubscription = new Subscription()
  logoutWarningSubscription = new Subscription()
  refreshInProgress: boolean = false
  refreshTokenObservers: any = []
  activeCookieDomain:string = environment.ACTIVE_TIME_DOMAIN
  timer: any
  warningTimeBefore: number = 5*60
  refreshTokenDefaultExpiry: number = 12*60*60
  warningSnackBarRef: MatSnackBarRef<SnackbarComponent>

  constructor(
    private httpClient: HttpClient, 
    private cookieService: CookieService, 
    private router: Router,
    private _snackBar: MatSnackBar
  ) { }

  get(url: string, params: any = {}, useAuth: boolean = true, useLidarURL: boolean = false): Observable<any> {
    return new Observable((observer) =>
    {
      this.getAccessToken()
      .pipe(
        catchError((err) => {
          return throwError(err)
        })
      )
      .subscribe((accessToken: any) => {
        if(!accessToken) {
          this.logout()
          return observer.next(null)
        }

        let headers = new HttpHeaders()
        accessToken = this.cookieService.get('accessToken')
        if (useAuth) {
          if (accessToken) {
            headers = headers.set('Authorization', `Bearer ${accessToken}`)
          } else {
            console.warn("No Access Token found")
          }
        }
        const httpOptions = {
          headers: headers,
          params: params
        }
        return this.httpClient.get((useLidarURL ? this.lidarBaseUrl : this.baseUrl) + url, httpOptions).pipe(
          catchError((err) => {
            console.log('error caught in service')
            if (err.status == 401) {
              this.logout()
            }
            console.error(err.status, 'Status**');
            return throwError(err);
          })
        ).subscribe((data: any) => {
          return observer.next(data)
        })
      })
    }) 
  }

  getBlob(url: string, params: any = {}, useAuth: boolean = true, useLidarURL: boolean = false): Observable<HttpResponse<Blob> | null> {
    return new Observable<HttpResponse<Blob> | null>((observer) => {
      const sub = this.getAccessToken()
        .pipe(
          catchError((err) => {
            return throwError(err);
          })
        )
        .subscribe((accessToken: any) => {
          if (!accessToken) {
            this.logout();
            observer.next(null);
            observer.complete();
            return;
          }

          let headers = new HttpHeaders();
          accessToken = this.cookieService.get('accessToken');
          if (useAuth) {
            if (accessToken) {
              headers = headers.set('Authorization', 'Bearer ' + accessToken);
            } else {
              console.warn("No Access Token found");
            }
          }
          const httpSub = this.httpClient.get((useLidarURL ? this.lidarBaseUrl : this.baseUrl) + url, {
            headers: headers,
            params: params,
            responseType: 'blob',
            observe: 'response'
          }).pipe(
            catchError((err) => {
              if (err.status == 401) {
                this.logout();
              }
              console.error(err.status, 'Status**');
              return throwError(err);
            })
          ).subscribe(
            (response: HttpResponse<Blob>) => {
              observer.next(response);
              observer.complete();
            },
            (err: any) => observer.error(err)
          );
          sub.add(httpSub);
        });
      return () => sub.unsubscribe();
    });
  }

  post(url: string, data: any, useAuth: boolean = true, formData: boolean = false, useLidarURL: boolean = false): Observable<any> {
    return new Observable((observer) => {
      console.log('HTTPService: post ', url)
      const refreshToken = this.cookieService.get('refreshToken')
      if (refreshToken) {
        this.getAccessToken()
        .pipe(
          catchError((err) => {
            return throwError(err)
          })
        )
        .subscribe((accessToken: any) => {
          if(!accessToken) {
            this.logout()
            return observer.next(null)
          }
          this.postApiCall(url, data, useAuth, formData, useLidarURL)
            .subscribe((data:any) => {
              return observer.next(data)
            })
        })
      }
      //for login API
      else {
        this.postApiCall(url, data, useAuth, formData, useLidarURL)
          .subscribe((data:any) => {
            return observer.next(data)
          })
      }
    })
  }

  postApiCall(url: string, data: any, useAuth: boolean = true, formData: boolean = false, useLidarURL: boolean = false): Observable<any> {
    let headers = new HttpHeaders({
      'Content-Type': 'application/json',
    })
    const accessToken = this.cookieService.get("accessToken")
    if (useAuth) {
      if (accessToken) {
        headers = headers.set('Authorization', `Bearer ${accessToken}`)
      }
    }
    if (formData) {
      headers = new HttpHeaders()
      headers = headers.set('Authorization', `Bearer ${accessToken}`)
    }
    const httpOptions = {
      headers: headers
    };
    return this.httpClient.post((useLidarURL ? this.lidarBaseUrl : this.baseUrl) + url, data, httpOptions).pipe(
      catchError((err) => {
        console.log('error caught in service')
        if (err.status == 401) {
          this.logout()
        }
        console.error(err);
        return throwError(err);
      })
    )
  }

  logout() {
    this.tokenSubscription.unsubscribe();
    // this.store.dispatch(logoutAction())
    if(this.fromLogoutPage) {
      // this.router.navigate(['logout'])
      this.cookieService.delete('showInactive')
      this.cookieService.delete('accessToken')
      this.cookieService.delete('clientId')
      this.cookieService.delete('courseId')
      this.cookieService.delete('selectedPinsheetDate')
      this.cookieService.delete('pinsheetDate')
      this.cookieService.delete('loggedInUserId')
      this.cookieService.delete('refreshToken')
      this.cookieService.delete('idToken')
      this.cookieService.delete('userId')
      this.cookieService.delete('lastActive', undefined, this.activeCookieDomain)
      window.location.reload()
    }
    else {
      let idToken = this.cookieService.get('idToken')
      if(idToken) {
        window.location.href = `${environment.IDP_URL}/connect/endsession?id_token_hint=${idToken}&post_logout_redirect_uri=${window.location.origin}/logout`
      }
      else {
        // this.router.navigate(['logout'])
        this.cookieService.delete('showInactive')
        this.cookieService.delete('accessToken')
        this.cookieService.delete('clientId')
        this.cookieService.delete('courseId')
        this.cookieService.delete('selectedPinsheetDate')
        this.cookieService.delete('pinsheetDate')
        this.cookieService.delete('loggedInUserId')
        this.cookieService.delete('refreshToken')
        this.cookieService.delete('idToken')
        this.cookieService.delete('userId')
        this.cookieService.delete('lastActive', undefined, this.activeCookieDomain)
        this.router.navigate(['logout'])
      }
    }
  }

  refresh(refreshToken: string) {
    let headers = new HttpHeaders({
      'Content-Type': 'application/json',
    })

    const httpOptions = {
      headers: headers
    }

    return this.httpClient.get(this.baseUrl + `/api/users/refresh?refreshToken=${refreshToken}`, httpOptions)
  }

  getAccessToken(): Observable<any> {
    return new Observable((observer) => {
      let accessToken = this.cookieService.get('accessToken')
      if(!accessToken) {
        return observer.next(null);
      }
      // const expiry = (JSON.parse(atob(accessToken.split('.')[1]))).exp
      if (this.getTokenExpiry(accessToken) > 120) {
        return observer.next(accessToken)
      }

      // access token will expire soon, refresh it
      const refreshToken = this.cookieService.get('refreshToken')
      if(!refreshToken) {
        return observer.next(null);
      }

      if(!this.refreshInProgress) {
        this.refreshTokenObservers = []
        this.refreshTokenObservers.push(observer)
        this.refreshInProgress = true
        this.refresh(refreshToken)
          .pipe(
            catchError((err) => {
              console.error('refreshToken call failed', err)
              if (err.status == 401) {
                this.logout()
              }
              return throwError(err)
            })
          )
          .subscribe((data: any) => {
            this.refreshInProgress = false
            if (data.Status == 'Success') {
              this.setTokensAndCounter('accessToken', 'refreshToken', data)
              this.setLastActiveCookie()
              // return observer.next(data.AccessToken)
              this.refreshTokenObservers.map((o: any) => o.next(data.AccessToken))
            } else {
              console.error("refreshToken API returned error", data)
              return observer.next(null)
            }
          })
      }
      else {
        this.refreshTokenObservers.push(observer)
      }
    })
  }

  getTokenExpiry(token: string) {
    return JSON.parse(atob(token.split('.')[1])).exp - (Math.floor((new Date).getTime() / 1000))
  }

  refreshExpirationCounter(timeout: any) {
    console.log(timeout, '***timeout')
    this.tokenSubscription.unsubscribe();
    this.tokenSubscription = of(null).pipe(delay(timeout)).subscribe((expired) => {
      console.log('EXPIRED!!');
      this.tokenExpiredCallback();
    });
    this.logoutWarningCounter(timeout - this.warningTimeBefore*1000)
  }

  logoutWarningCounter(timeout: any) {
    console.log(timeout, '***Warning-timeout')
    this.logoutWarningSubscription.unsubscribe();
    this.clearTimer();
    this.dismissSnackBar();
    this.logoutWarningSubscription = of(null).pipe(delay(timeout)).subscribe((warned) => {
      console.log("warned!!")
      let lastActive = this.cookieService.get('lastActive')
      if(lastActive) {
        console.log('Has Last Active')
        let currenttime = (new Date).getTime()
        let diff = (currenttime - (+lastActive))/1000

        if(diff >= (this.refreshTokenDefaultExpiry - this.warningTimeBefore) && diff < this.refreshTokenDefaultExpiry) {
          this.openWarningSnackBar(this.refreshTokenDefaultExpiry - diff)
        }
        else {
          console.log(diff, " Difference between current time and last active")
        }
      }
      else {
        console.log('No Last Active')
        let refreshToken = this.cookieService.get('refreshToken')
        console.log(this.getTokenExpiry(refreshToken))
        this.openWarningSnackBar(this.getTokenExpiry(refreshToken))
      }
    })
  }

  getWarningMessage(time: any) : string {
    let message = `You will be logged out within`
    let minutes = Math.floor(time/60)
    let seconds = Math.floor(time%60)
    if(minutes > 0) {
      message = message + ` ${minutes} minute${minutes > 1 ? 's' : ''}`
    }
    if(seconds > 0) {
      message = message + ` ${seconds} second${seconds > 1 ? 's': ''}`
    }
    return message
  }

  getSeconds() {
    let seconds
    let lastActive = this.cookieService.get('lastActive')
    if(lastActive) {
      let currenttime = (new Date).getTime()
      seconds = (this.refreshTokenDefaultExpiry) - (currenttime - (+lastActive))/1000
    }
    else {
      let refreshToken = this.cookieService.get('refreshToken')
      seconds = this.getTokenExpiry(refreshToken)
    }

    return seconds
  }

  openWarningSnackBar(seconds: number) {
    this.warningSnackBarRef = this._snackBar.openFromComponent(SnackbarComponent, {
      data: {
        message: this.getWarningMessage(seconds),
        actions: ['Dismiss', 'Keep me logged in'],
        actionClick: (action: string) => this.warningSnackbarActionClicked(action)
      },
      duration: seconds * 1000,
      horizontalPosition: "end",
      verticalPosition: "top",
      panelClass: 'warningSnack'
    })

    this.timer = setInterval(() => {
      seconds = this.getSeconds()
      if(seconds > 0 && seconds <= this.warningTimeBefore) {
        this.warningSnackBarRef.instance.data.message = this.getWarningMessage(seconds)
      }
      else {
        this.clearTimer()
        this.dismissSnackBar()
      }
    }, 1000)
  }

  warningSnackbarActionClicked(action: string) {
    if(action == 'Dismiss') {
      console.log('Dismiss Clicked')
    }
    else if(action == 'Keep me logged in') {
      console.log('Keep me logged in Clicked')
      let refreshToken = this.cookieService.get('refreshToken')

      this.refresh(refreshToken)
      .pipe(
        catchError((err) => {
          console.error('refreshToken call failed', err)
          if (err.status == 401) {
            this.logout()
          }
          return throwError(err)
        })
      )
      .subscribe((data: any) => {
        this.refreshInProgress = false
        if (data.Status == 'Success') {
          this.setTokensAndCounter('accessToken', 'refreshToken', data)
          this.setLastActiveCookie()
        } else {
          console.error("refreshToken API returned error", data)
        }
      })

    }
    this.clearTimer()
    this.dismissSnackBar()
  }

  setTokensAndCounter(accessTokenName: string, refreshTokenName: string, data: any) {
    this.cookieService.set(accessTokenName, data.AccessToken)
    this.cookieService.set(refreshTokenName, data.RefreshToken)
    this.refreshExpirationCounter(this.getTokenExpiry(data.RefreshToken)*1000)
  }

  tokenExpiredCallback() {
    let lastActive = this.cookieService.get('lastActive')
    let currenttime = (new Date).getTime()
    let diff = currenttime - (+lastActive)
    
    if(diff/1000 < this.refreshTokenDefaultExpiry) {
      this.cookieService.delete('accessToken')
      this.cookieService.delete('refreshToken')
      this.cookieService.delete('idToken')
      this.router.navigate(['login'])
    }
    else {
      this.logout();
    }
  }

  setLastActiveCookie() {
    let epochTime = (new Date).getTime()
    this.cookieService.set('lastActive', epochTime.toString(), { domain: this.activeCookieDomain })
  }

  clearTimer() {
    if(this.timer) {
      clearInterval(this.timer)
    }
  }

  dismissSnackBar() {
    if(this.warningSnackBarRef) {
      this.warningSnackBarRef.dismiss();
    }
  }

}